import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'react-redux';
import Loading from '../../components/Loading';
import NavBar from '../../components/AppBar/NavBar';
import Footer from '../../components/Footer/Footer';
import HideChatWidget from '../../components/ZendeskChat/HideChatWidget';
import { MarketingOptInProvider } from '../../contexts/MarketingOptInContext';
import { CheckoutContextProvider } from '../../lib/CheckoutContext';
import * as events from '../../lib/analytics/events';
import { getNanoVariantPetName, hasNanoVariantPetName } from '../../lib/util/petUrlParams';
import { useSpeciesUrlParam } from '../../hooks/useSpeciesUrlParam';
import useRescueActivationFee from '../../hooks/useRescueActivationFee';
import useRescueCartSeed from '../../hooks/useRescueCartSeed';
import useRescueCouponReady from '../../hooks/useRescueCouponReady';
import * as types from '../../types';
import { Species } from '../../types/gql-op-types';
import { StackedStripeCheckout } from '../Checkout/pages/StripeCheckout/StripeCheckoutPage';
import EscapeAlert from './EscapeAlert';
import styles from './RescueCheckout.module.scss';

/** Whether any part of the element is in the viewport. False until it has been measured. */
function useOnScreen(ref: RefObject<HTMLElement>) {
  const [onScreen, setOnScreen] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setOnScreen(!!entry?.isIntersecting));
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return onScreen;
}

/**
 * The glyphs from the Nano checkout's "What {pet} gets": one ink colour on a near-black disc, so the
 * lines tell apart by shape and colour stays reserved for the pay button.
 */
function BenefitIcon({ name }: { name: 'location' | 'alert' | 'steps' | 'health' | 'community' }) {
  return (
    <svg className={styles.benefitIcon} width="22" height="22" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="10" fill="#100f0e" />
      {name === 'location' && (
        <>
          <path
            d="M10 4.5c-2 0-3.3 1.5-3.3 3.3 0 2.3 3.3 6.2 3.3 6.2s3.3-3.9 3.3-6.2c0-1.8-1.3-3.3-3.3-3.3z"
            fill="#ffffff"
          />
          <circle cx="10" cy="7.8" r="1.3" fill="#100f0e" />
        </>
      )}
      {name === 'alert' && (
        <>
          <path d="M10 4.5c-2 0-3.2 1.5-3.2 3.4v2.2L5.8 12h8.4l-1-1.9V7.9c0-1.9-1.2-3.4-3.2-3.4z" fill="#ffffff" />
          <circle cx="10" cy="14.2" r="1.2" fill="#ffffff" />
        </>
      )}
      {name === 'steps' && (
        <>
          <ellipse cx="7.5" cy="8" rx="1.6" ry="2.3" fill="#ffffff" />
          <ellipse cx="12.5" cy="12" rx="1.6" ry="2.3" fill="#ffffff" />
        </>
      )}
      {name === 'health' && (
        <path
          d="M4 10.5h3l1.5-3.5 2.5 6.5 1.5-3H16"
          fill="none"
          stroke="#ffffff"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {name === 'community' && (
        <>
          <circle cx="7.5" cy="8" r="1.8" fill="#ffffff" />
          <circle cx="12.5" cy="8" r="1.8" fill="#ffffff" />
          <path d="M4.5 14c0-2 1.4-3 3-3s3 1 3 3zM9.5 14c0-2 1.4-3 3-3s3 1 3 3z" fill="#ffffff" />
        </>
      )}
    </svg>
  );
}

/** Proof before the price: dogs get the lost-dogs-found figure with a recovery story; cats, whom the figure is not about, a cat owner's recovery story. */
function ProofCard({ species }: { species: Species }) {
  const { t } = useTranslation();

  if (species === Species.CAT) {
    return (
      <figure className={styles.proof}>
        <span className={styles.proofLabel}>{t('rescue_checkout_cat_proof_label')}</span>
        <blockquote className={styles.proofQuote}>{t('rescue_checkout_cat_proof_quote')}</blockquote>
        <figcaption className={styles.proofAttribution}>{t('rescue_checkout_cat_proof_attribution')}</figcaption>
      </figure>
    );
  }

  return (
    <figure className={styles.proof}>
      <div className={styles.proofStat}>
        <svg width="40" height="40" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="10" cy="10" r="10" fill="#14cd71" />
          <path
            d="M10 4.5c-2 0-3.3 1.5-3.3 3.3 0 2.3 3.3 6.2 3.3 6.2s3.3-3.9 3.3-6.2c0-1.8-1.3-3.3-3.3-3.3z"
            fill="#100f0e"
          />
          <circle cx="10" cy="7.8" r="1.3" fill="#14cd71" />
        </svg>
        <div className={styles.proofStatText}>
          <span className={styles.proofStatValue}>{t('rescue_stat_value')}</span>
          <span className={styles.proofStatLabel}>{t('rescue_stat_label')}</span>
        </div>
      </div>
      <div className={styles.proofRule} />
      <blockquote className={styles.proofQuote}>{t('rescue_testimonial_recovery_quote')}</blockquote>
      <figcaption className={styles.proofAttribution}>{t('rescue_testimonial_recovery_attribution')}</figcaption>
    </figure>
  );
}

/**
 * Every line that names the pet, in its named and generic form. Most adopter links carry no usable
 * `pet_name`, and "your dog left Home" reads as a mistake, so those adopters get copy written without a
 * name rather than the name helper's "your dog" fallback dropped into a named sentence.
 */
function usePetCopy(species: Species) {
  const { t } = useTranslation();
  const cat = species === Species.CAT;

  if (hasNanoVariantPetName()) {
    const petName = getNanoVariantPetName(species) ?? '';
    return {
      eyebrow: t('rescue_checkout_eyebrow', { petName }),
      title: t('rescue_checkout_title', { petName }),
      alertBody: t('rescue_checkout_alert_body', { petName }),
      notificationLabel: t('rescue_checkout_notification_label', { petName }),
      notificationTitle: t('rescue_checkout_notification_title', { petName }),
      notificationBody: t('rescue_checkout_notification_body', { petName }),
      benefitsTitle: t('rescue_checkout_benefits_title', { petName }),
      benefitSteps: t('rescue_checkout_benefit_steps', { petName }),
      benefitActivity: t('rescue_checkout_benefit_activity', { petName }),
      barCta: t('rescue_checkout_bar_cta', { petName }),
    };
  }

  return {
    eyebrow: t('rescue_checkout_eyebrow_generic'),
    title: cat ? t('rescue_checkout_title_generic_cat') : t('rescue_checkout_title_generic_dog'),
    alertBody: t('rescue_checkout_alert_body_generic'),
    notificationLabel: t('rescue_checkout_notification_label_generic'),
    notificationTitle: t('rescue_checkout_notification_title_generic'),
    notificationBody: cat
      ? t('rescue_checkout_notification_body_generic_cat')
      : t('rescue_checkout_notification_body_generic_dog'),
    benefitsTitle: t('rescue_checkout_benefits_title_generic'),
    benefitSteps: t('rescue_checkout_benefit_steps_generic'),
    benefitActivity: t('rescue_checkout_benefit_activity_generic'),
    barCta: t('rescue_checkout_bar_cta_generic'),
  };
}

/**
 * The rescue offer as one page: what the tracker does for the adopter's pet, then the summary and the
 * checkout, with no cart in between. Built from what converts on the Nano checkout (Oct 2026): the
 * escape-alert clip first, "What {pet} gets" ahead of the price, the price stated once in the summary,
 * and payment on the same screen.
 *
 * The checkout is the shop's own Stripe checkout, embedded stacked, so pricing, the adoption coupon,
 * tax, shipping, wallets, 3DS and the thank-you page all behave exactly as they do from the cart.
 */
export default function RescueCheckout() {
  const { t } = useTranslation();
  const store = useStore<types.AppState>();
  const [species] = useSpeciesUrlParam();
  const copy = usePetCopy(species);
  const fee = useRescueActivationFee();
  const seeded = useRescueCartSeed(species);
  // prices only once the adoption coupon is on the cart, so nobody sees or pays the regular price
  const couponReady = useRescueCouponReady();
  const checkoutRef = useRef<HTMLElement>(null);
  const checkoutOnScreen = useOnScreen(checkoutRef);

  // the cart's Checkout button reports the checkout as started (analytics, Customer.io, ad pixels);
  // with no cart in between, this page reports it when its checkout first shows a price
  const reportCheckoutStarted = useCallback(
    (pricing: types.CartPricing) => {
      const { cart } = store.getState().storeShop;
      events.cartPage.checkout(cart.cartItems, pricing, cart.couponCode, cart.isGift);
    },
    [store],
  );

  // cats get activity but not steps, sleep or health trends, which Fi tracks for dogs only
  const benefits =
    species === Species.CAT
      ? ([
          ['location', t('rescue_checkout_benefit_location')],
          ['alert', t('rescue_checkout_benefit_alert')],
          ['steps', copy.benefitActivity],
          ['community', t('rescue_checkout_benefit_community')],
        ] as const)
      : ([
          ['location', t('rescue_checkout_benefit_location')],
          ['alert', t('rescue_checkout_benefit_alert')],
          ['steps', copy.benefitSteps],
          ['health', t('rescue_checkout_benefit_health')],
          ['community', t('rescue_checkout_benefit_community')],
        ] as const);

  return (
    <div className={styles.page}>
      {/* the nav floats over the pdp's gallery; here it gets a bar of its own under the rescue banner */}
      <div className={styles.navSlot}>
        <NavBar />
      </div>
      {/* a div: the app shell already provides the page's <main> */}
      <div className={styles.main}>
        <div className={styles.grid}>
          <section className={styles.story} aria-labelledby="rescue-checkout-title">
            <div className={styles.heading}>
              <span className={styles.eyebrow}>{copy.eyebrow}</span>
              <h1 id="rescue-checkout-title" className={styles.title}>
                {copy.title}
              </h1>
              <p className={styles.subtitle}>{t('rescue_checkout_subtitle')}</p>
            </div>

            <EscapeAlert
              species={species}
              label={copy.notificationLabel}
              title={copy.notificationTitle}
              body={copy.notificationBody}
            >
              <span className={styles.alertEyebrow}>{t('rescue_checkout_alert_eyebrow')}</span>
              <span className={styles.alertHeadline}>
                {species === Species.CAT
                  ? t('rescue_checkout_alert_headline_cat')
                  : t('rescue_checkout_alert_headline_dog')}
              </span>
              <span className={styles.alertBody}>{copy.alertBody}</span>
            </EscapeAlert>

            <div className={styles.card}>
              <h2 className={styles.cardTitle}>{copy.benefitsTitle}</h2>
              <ul className={styles.benefits}>
                {benefits.map(([icon, text]) => (
                  <li key={icon} className={styles.benefit}>
                    <BenefitIcon name={icon} />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <ProofCard species={species} />
          </section>

          <section className={styles.checkout} ref={checkoutRef} aria-labelledby="rescue-checkout-form-title">
            <div className={styles.checkoutHeading}>
              <span className={styles.eyebrow}>{t('rescue_checkout_form_eyebrow')}</span>
              <h2 id="rescue-checkout-form-title" className={styles.checkoutTitle}>
                {t('rescue_checkout_form_title')}
              </h2>
            </div>
            {seeded && couponReady ? (
              <MarketingOptInProvider>
                <CheckoutContextProvider>
                  <StackedStripeCheckout onPriced={reportCheckoutStarted} />
                </CheckoutContextProvider>
              </MarketingOptInProvider>
            ) : (
              <Loading />
            )}
          </section>
        </div>
      </div>

      {/* phones only, and only until the checkout is on screen: the checkout's own place-order button
          takes over from there, so one yellow button shows at a time */}
      {!checkoutOnScreen && (
        <div className={styles.bar}>
          {fee && (
            <div className={styles.barTotal}>
              <span className={styles.barLabel}>{t('rescue_checkout_bar_label')}</span>
              <span className={styles.barAmount}>{fee}</span>
            </div>
          )}
          <button
            type="button"
            className={styles.barButton}
            onClick={() => checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {copy.barCta}
          </button>
        </div>
      )}

      <Footer />
      {/* as on the pdp: the chat launcher would sit on top of the pay bar */}
      <HideChatWidget />
    </div>
  );
}
